import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import PhoneInput from '../../components/common/PhoneInput';

const AdditionalCustomersSection = ({
    customers = [],
    onChange,
    errors = {},
    disabled = false
}) => {
    const [localCustomers, setLocalCustomers] = useState(customers);

    // Sync with parent when customers prop changes
    useEffect(() => {
        setLocalCustomers(customers);
    }, [customers]);

    // Stable debounce for parent updates
    const debouncedUpdate = useMemo(() => {
        let timeoutId;
        return (newCustomers) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                onChange?.(newCustomers);
            }, 300);
        };
    }, [onChange]);

    const addCustomer = useCallback(() => {
        const newCustomers = [...localCustomers, { name: '', email: '', phone: '' }];
        setLocalCustomers(newCustomers);
        debouncedUpdate(newCustomers);
    }, [localCustomers, debouncedUpdate]);

    const removeCustomer = useCallback((index) => {
        const newCustomers = localCustomers.filter((_, i) => i !== index);
        setLocalCustomers(newCustomers);
        debouncedUpdate(newCustomers);
    }, [localCustomers, debouncedUpdate]);

    const updateCustomer = useCallback((index, field, value) => {
        const newCustomers = localCustomers.map((customer, i) =>
            i === index ? { ...customer, [field]: value } : customer
        );
        setLocalCustomers(newCustomers); // ✅ instant local update for smooth typing
        debouncedUpdate(newCustomers);   // ✅ parent updates after debounce
    }, [localCustomers, debouncedUpdate]);

    return (
        <Box>
            {localCustomers.map((customer, index) => (
                <Box 
                    key={`customer-${index}`} 
                    sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Name"
                                value={customer.name || ''}
                                onChange={(e) => updateCustomer(index, 'name', e.target.value)}
                                fullWidth
                                size="small"
                                disabled={disabled}
                                error={!!errors[`additional_customer_${index}_name`]}
                                helperText={errors[`additional_customer_${index}_name`] || ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Email"
                                value={customer.email || ''}
                                onChange={(e) => updateCustomer(index, 'email', e.target.value)}
                                fullWidth
                                size="small"
                                disabled={disabled}
                                placeholder="Optional"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <PhoneInput
                                label="Phone"
                                value={customer.phone || ''}
                                onChange={(val) => updateCustomer(index, 'phone', val)}
                                size="small"
                                disabled={disabled}
                                required={false}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => removeCustomer(index)}
                                startIcon={<DeleteIcon />}
                                size="small"
                                disabled={disabled}
                            >
                                Remove
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            ))}
            <Button
                variant="outlined"
                onClick={addCustomer}
                startIcon={<AddIcon />}
                size="small"
                disabled={disabled}
            >
                Add Customer
            </Button>
        </Box>
    );
};

export default AdditionalCustomersSection;
